import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import { BottomSheet } from "@/components/BottomSheet";
import { fmtDate } from "@/lib/format";
import InstallPWA from "@/components/InstallPWA";
import { Crown, Clock, Lock, Pencil, Upload, LogOut, FileText, Shield, ChevronRight, KeyRound, Building2, TriangleAlert } from "lucide-react";

const Box = ({ testid, ...p }) => <input data-testid={testid} {...p} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-3.5 py-3 text-base focus:outline-none focus:border-brandPrimary placeholder:text-muted-foreground" />;

const carrierExpiryDays = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

export default function Account() {
  const navigate = useNavigate();
  const { user, access, logout, setUser, setAccess, checkAuth } = useAuth();
  const [sp, setSp] = useState(useSearchParams()[0]);
  const [trades, setTrades] = useState([]);
  const [logo, setLogo] = useState(null);
  const [editSheet, setEditSheet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [form, setForm] = useState({ business_name: "", phone: "", legal: {}, address: {}, vat_registered: false, vat_number: "", carrier_licence_expiry: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Payment received — welcome to Docket Pro!");
      let tries = 0;
      const iv = setInterval(async () => { tries++; await checkAuth(); if (tries >= 4) clearInterval(iv); }, 1500);
      window.history.replaceState(null, "", "/account");
    } else if (params.get("checkout") === "cancel") {
      window.history.replaceState(null, "", "/account");
    }
  }, []);

  useEffect(() => {
    api.get("/config/trades").then(({ data }) => setTrades(data.trades));
    api.get("/me/logo-data").then(({ data }) => setLogo(data.data_url)).catch(() => {});
  }, []);

  useEffect(() => { if (user) setForm({ business_name: user.business_name || "", phone: user.phone || "", legal: user.legal || {}, address: user.address || {}, vat_registered: !!user.vat_registered, vat_number: user.vat_number || "", carrier_licence_expiry: user.carrier_licence_expiry || "" }); }, [user]);

  const tradeObj = trades.find((t) => t.key === user?.trade);
  const legalFields = tradeObj?.legal_fields || [];

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/logo", fd); const { data } = await api.get("/me/logo-data"); setLogo(data.data_url); toast.success("Logo updated"); }
    catch { toast.error("Upload failed"); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.patch("/profile", form); setUser(data.user); setAccess(data.access); toast.success("Saved"); setEditSheet(false); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const subscribe = async () => {
    setBillingBusy(true);
    try { const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start checkout — please try again"); setBillingBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current period.")) return;
    setBillingBusy(true);
    try { const { data } = await api.post("/billing/cancel"); setAccess(data.access); await checkAuth(); toast.success("Will cancel at period end"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel — please try again"); }
    finally { setBillingBusy(false); }
  };
  const resume = async () => {
    setBillingBusy(true);
    try { const { data } = await api.post("/billing/resume"); setAccess(data.access); await checkAuth(); toast.success("Subscription resumed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't resume — please try again"); }
    finally { setBillingBusy(false); }
  };

  if (!user || !access) return <Shell showNav title="Account"><div className="h-40 bg-surfaceTertiary rounded-[12px] animate-pulse" /></Shell>;

  return (
    <Shell showNav title="Account">
      <div className="flex items-center gap-3 animate-fade-up">
        <div className="relative">
          {logo ? <img src={logo} alt="" className="w-16 h-16 rounded-[16px] object-contain border border-border bg-surface" /> : <div className="w-16 h-16 rounded-[16px] bg-brandTertiary flex items-center justify-center"><Building2 size={26} className="text-brandSecondary" /></div>}
          <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center cursor-pointer active:scale-90 transition-transform">
            <Upload size={13} /><input data-testid="account-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
          </label>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg font-extrabold text-onSurface truncate">{user.business_name}</p>
          <p className="text-sm text-muted-foreground truncate">{user.trade_label} · {user.email}</p>
        </div>
      </div>

      {/* Plan card */}
      <BillingCard access={access} user={user} onSubscribe={subscribe} onCancel={cancel} onResume={resume} busy={billingBusy} />

      {(() => {
        const days = carrierExpiryDays(user.carrier_licence_expiry);
        if (days === null || days > 30) return null;
        return (
          <div data-testid="carrier-expiry-warning" className={`mt-4 rounded-[16px] p-4 flex items-start gap-3 ${days < 0 ? "bg-error/10 border border-error/30" : "bg-brandTertiary border border-brandPrimary/30"}`}>
            <TriangleAlert size={18} className={days < 0 ? "text-error shrink-0 mt-0.5" : "text-brandPrimary shrink-0 mt-0.5"} />
            <div>
              <p className="font-heading font-bold text-sm text-onSurface">{days < 0 ? "Waste carrier licence expired" : "Waste carrier licence expiring soon"}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{days < 0 ? `It expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago. Renew it with the Environment Agency to keep carrying waste legally.` : `Only ${days} day${days === 1 ? "" : "s"} left. Renew it with the Environment Agency before it lapses.`}</p>
            </div>
          </div>
        );
      })()}

      <InstallPWA />

      {/* Menu */}
      <div className="mt-4 bg-surface rounded-[16px] border border-border divide-y divide-border overflow-hidden">
        <Item testid="edit-business" icon={Pencil} label="Business details" onClick={() => setEditSheet(true)} />
        <Item testid="account-terms" icon={FileText} label="Terms & Conditions" onClick={() => navigate("/legal/terms")} />
        <Item testid="account-privacy" icon={Shield} label="Privacy Policy" onClick={() => navigate("/legal/privacy")} />
      </div>

      <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="w-full mt-4 mb-2 bg-surface border border-border rounded-[16px] p-4 flex items-center justify-center gap-2 text-error font-semibold active:scale-[0.99] transition-transform"><LogOut size={18} /> Log out</button>
      <p className="text-center text-xs text-muted-foreground mt-2">Docket · v1.0</p>

      <BottomSheet open={editSheet} onClose={() => setEditSheet(false)} title="Business details">
        <div className="flex flex-col gap-3">
          <div><p className="text-xs font-bold text-muted-foreground mb-1">Business name</p><Box testid="acc-business-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
          <div><p className="text-xs font-bold text-muted-foreground mb-1">Phone</p><Box testid="acc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          {legalFields.map((f) => (
            <div key={f.key}><p className="text-xs font-bold text-muted-foreground mb-1">{f.label}{f.required && <span className="text-error"> *</span>}</p><Box testid={`acc-legal-${f.key}`} value={form.legal[f.key] || ""} onChange={(e) => setForm({ ...form, legal: { ...form.legal, [f.key]: e.target.value } })} /></div>
          ))}
          <div><p className="text-xs font-bold text-muted-foreground mb-1">Waste carrier licence expiry</p><Box testid="acc-carrier-expiry" type="date" value={form.carrier_licence_expiry} onChange={(e) => setForm({ ...form, carrier_licence_expiry: e.target.value })} /></div>
          <div className="flex items-center justify-between bg-surfaceSecondary border border-border rounded-[12px] px-3.5 py-3">
            <div><p className="text-sm font-bold text-onSurface">VAT registered</p><p className="text-xs text-muted-foreground">Adds VAT per line on quotes & invoices</p></div>
            <button data-testid="acc-vat-toggle" onClick={() => setForm({ ...form, vat_registered: !form.vat_registered })} className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${form.vat_registered ? "bg-brandPrimary" : "bg-borderStrong"}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${form.vat_registered ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          {form.vat_registered && <div><p className="text-xs font-bold text-muted-foreground mb-1">VAT number</p><Box testid="acc-vat-number" placeholder="e.g. GB 123 4567 89" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>}
          <p className="text-xs font-bold text-muted-foreground mt-1">Address</p>
          <Box testid="acc-addr1" placeholder="Line 1" value={form.address.line1 || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })} />
          <Box testid="acc-addr-city" placeholder="Town / City" value={form.address.city || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} />
          <Box testid="acc-addr-postcode" placeholder="Postcode" value={form.address.postcode || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, postcode: e.target.value } })} />
          <button data-testid="acc-save" disabled={saving} onClick={saveProfile} className="mt-2 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold active:scale-[0.98] transition-transform disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </BottomSheet>
    </Shell>
  );
}

const BillingCard = ({ access, user, onSubscribe, onCancel, onResume, busy }) => {
  if (access.subscribed) {
    return (
      <div data-testid="billing-card" className="mt-4 bg-onSurface text-onBrand rounded-[20px] p-5 animate-fade-up">
        <div className="flex items-center gap-2"><Crown size={20} className="text-brandPrimary" /><span className="font-heading font-bold">Docket Pro</span><span className="ml-auto text-xs bg-success/20 text-green-300 px-2 py-0.5 rounded-full">Active</span></div>
        <p className="text-sm text-white/70 mt-2">£12/month{access.current_period_end ? ` · ${access.cancel_at_period_end ? "ends" : "renews"} ${fmtDate(access.current_period_end)}` : ""}</p>
        {access.cancel_at_period_end ? (
          <>
            <p className="text-xs text-brandPrimary mt-2">Set to cancel at period end.</p>
            <button data-testid="resume-btn" disabled={busy} onClick={onResume} className="w-full mt-3 bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50">Resume subscription</button>
          </>
        ) : (
          <button data-testid="cancel-btn" disabled={busy} onClick={onCancel} className="w-full mt-3 bg-white/10 text-white rounded-full h-11 font-semibold text-sm active:scale-[0.98] transition-transform disabled:opacity-50">Cancel subscription</button>
        )}
      </div>
    );
  }
  if (access.trial_active) {
    return (
      <div data-testid="billing-card" className="mt-4 bg-surface border border-border rounded-[20px] p-5 animate-fade-up">
        <div className="flex items-center gap-2"><Clock size={20} className="text-brandPrimary" /><span className="font-heading font-bold text-onSurface">Free trial</span><span className="ml-auto text-xs font-bold bg-brandTertiary text-brandSecondary px-2 py-0.5 rounded-full">Day {access.trial_day} of {access.trial_days_total}</span></div>
        <p className="text-sm text-muted-foreground mt-2"><span className="font-bold text-onSurface">{access.trial_days_left} day{access.trial_days_left === 1 ? "" : "s"} left</span> of your free trial — then £12/month.</p>
        <button data-testid="subscribe-btn" disabled={busy} onClick={onSubscribe} className="w-full mt-3 bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"><Crown size={16} /> {busy ? "Starting…" : "Subscribe now — £12/mo"}</button>
        <p className="text-xs text-muted-foreground mt-2 text-center">Cancel anytime · secure payment by Stripe</p>
      </div>
    );
  }
  return (
    <div data-testid="billing-card" className="mt-4 bg-brandPrimary text-onBrand rounded-[20px] p-5 animate-fade-up">
      <div className="flex items-center gap-2"><Lock size={20} /><span className="font-heading font-bold">Trial ended</span></div>
      <p className="text-sm text-white/85 mt-2">Subscribe for £12/month to keep creating documents and using Kai. Cancel anytime.</p>
      <button data-testid="subscribe-btn" disabled={busy} onClick={onSubscribe} className="w-full mt-3 bg-white text-brandSecondary rounded-full h-11 font-heading font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50">{busy ? "Starting…" : "Subscribe now — £12/mo"}</button>
    </div>
  );
};

const Item = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 p-4 hover:bg-surfaceSecondary transition-colors text-left">
    <Icon size={18} className="text-muted-foreground" /><span className="flex-1 font-semibold text-sm text-onSurface">{label}</span><ChevronRight size={18} className="text-borderStrong" />
  </button>
);
